/**
 * UsageLogService
 * Service for UsageLog entity operations
 * Route: api/usage-logs
 */
import { BaseService } from '../base/BaseService';
/**
 * UsageLogService
 * Handles usage log CRUD operations and ModelView endpoints
 */
export class UsageLogService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UsageLogService.DEFAULT_SERVICE_PATH,
            basePath: UsageLogService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ============================================================
    // Entity CRUD Operations
    // ============================================================
    /**
     * Get all usage logs
     * @returns Promise resolving to array of usage logs
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all usage logs (raw response)
     * @returns Promise resolving to ResponseData with usage logs
     */
    getAllRaw() {
        const url = this.buildUrl('');
        return this.http.get(url);
    }
    /**
     * Get usage log by ID
     * @param id - Log ID
     * @returns Promise resolving to usage log or null
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    /**
     * Get usage log by ID (raw response)
     * @param id - Log ID
     * @returns Promise resolving to ResponseData with usage log
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('getById', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get usage logs by category ID
     * @param categoryId - Category ID
     * @returns Promise resolving to array of usage logs
     */
    async getByCategory(categoryId) {
        const response = await this.getByCategoryRaw(categoryId);
        return response.data ?? [];
    }
    /**
     * Get usage logs by category ID (raw response)
     * @param categoryId - Category ID
     * @returns Promise resolving to ResponseData with usage logs
     */
    getByCategoryRaw(categoryId) {
        const endpoint = this.getEndpoint('getByCategory', `category/${categoryId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get usage logs by course ID
     * @param courseId - Course ID
     * @returns Promise resolving to array of usage logs
     */
    async getByCourse(courseId) {
        const response = await this.getByCourseRaw(courseId);
        return response.data ?? [];
    }
    /**
     * Get usage logs by course ID (raw response)
     * @param courseId - Course ID
     * @returns Promise resolving to ResponseData with usage logs
     */
    getByCourseRaw(courseId) {
        const endpoint = this.getEndpoint('getByCourse', `course/${courseId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get usage logs by session ID
     * @param sessionId - Session ID
     * @returns Promise resolving to array of usage logs
     */
    async getBySession(sessionId) {
        const response = await this.getBySessionRaw(sessionId);
        return response.data ?? [];
    }
    /**
     * Get usage logs by session ID (raw response)
     * @param sessionId - Session ID
     * @returns Promise resolving to ResponseData with usage logs
     */
    getBySessionRaw(sessionId) {
        const endpoint = this.getEndpoint('getBySession', `session/${sessionId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new usage log
     * @param log - Log data
     * @returns Promise resolving to created usage log
     */
    async create(log) {
        const response = await this.createRaw(log);
        return response.data ?? null;
    }
    /**
     * Create a new usage log (raw response)
     * @param log - Log data
     * @returns Promise resolving to ResponseData with created usage log
     */
    createRaw(log) {
        const url = this.buildUrl('');
        return this.http.post(url, log);
    }
    /**
     * Update an existing usage log
     * @param id - Log ID
     * @param log - Updated log data
     * @returns Promise resolving to updated usage log
     */
    async update(id, log) {
        const response = await this.updateRaw(id, log);
        return response.data ?? null;
    }
    /**
     * Update an existing usage log (raw response)
     * @param id - Log ID
     * @param log - Updated log data
     * @returns Promise resolving to ResponseData with updated usage log
     */
    updateRaw(id, log) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, log);
    }
    /**
     * Delete a usage log
     * @param id - Log ID
     * @returns Promise resolving to true if successful
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete a usage log (raw response)
     * @param id - Log ID
     * @returns Promise resolving to ResponseData
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ============================================================
    // ModelView Endpoints
    // ============================================================
    /**
     * Get usage log view by ID
     * @param id - Log ID
     * @returns Promise resolving to usage log model view or null
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        return response.data ?? null;
    }
    /**
     * Get usage log view by ID (raw response)
     * @param id - Log ID
     * @returns Promise resolving to ResponseData with usage log model view
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('getView', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered usage log list view
     * @param filter - Optional filter criteria
     * @returns Promise resolving to array of usage log model views
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered usage log list view (raw response)
     * @param filter - Optional filter criteria
     * @returns Promise resolving to ResponseData with usage log model views
     */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || null);
    }
    /**
     * Get usage log views by category
     * @param categoryId - Category ID
     * @returns Promise resolving to array of usage log model views
     */
    async getCategoryViews(categoryId) {
        const response = await this.getCategoryViewsRaw(categoryId);
        return response.data ?? [];
    }
    /**
     * Get usage log views by category (raw response)
     * @param categoryId - Category ID
     * @returns Promise resolving to ResponseData with usage log model views
     */
    getCategoryViewsRaw(categoryId) {
        const endpoint = this.getEndpoint('getCategoryViews', `category/${categoryId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get usage log views by course
     * @param courseId - Course ID
     * @returns Promise resolving to array of usage log model views
     */
    async getCourseViews(courseId) {
        const response = await this.getCourseViewsRaw(courseId);
        return response.data ?? [];
    }
    /**
     * Get usage log views by course (raw response)
     * @param courseId - Course ID
     * @returns Promise resolving to ResponseData with usage log model views
     */
    getCourseViewsRaw(courseId) {
        const endpoint = this.getEndpoint('getCourseViews', `course/${courseId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get usage log views by session
     * @param sessionId - Session ID
     * @returns Promise resolving to array of usage log model views
     */
    async getSessionViews(sessionId) {
        const response = await this.getSessionViewsRaw(sessionId);
        return response.data ?? [];
    }
    /**
     * Get usage log views by session (raw response)
     * @param sessionId - Session ID
     * @returns Promise resolving to ResponseData with usage log model views
     */
    getSessionViewsRaw(sessionId) {
        const endpoint = this.getEndpoint('getSessionViews', `session/${sessionId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ============================================================
    // Ingest Endpoints (telemetry — anonymous-friendly)
    // ============================================================
    /**
     * Record that a session has been opened. Called once per mount of the
     * player page. Anonymous-friendly — anonymousID identifies pre-login
     * sessions; signed-in users are attributed via the JWT.
     * POST resources/api/usage-logs/open
     */
    async recordOpen(payload) {
        const response = await this.recordOpenRaw(payload);
        return response.success;
    }
    recordOpenRaw(payload) {
        const endpoint = this.getEndpoint('open', 'open');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, payload);
    }
    /**
     * Throttled heartbeat during playback. Coalesces server-side into the
     * live UsageLog row for the same session.
     * POST resources/api/usage-logs/ping
     */
    async recordPing(payload) {
        const response = await this.recordPingRaw(payload);
        return response.success;
    }
    recordPingRaw(payload) {
        const endpoint = this.getEndpoint('ping', 'ping');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, payload);
    }
    /**
     * Mark a session as completed. Stamps the per-user progress row and
     * surfaces course-level completion events server-side.
     * POST resources/api/usage-logs/complete
     */
    async recordCompleted(payload) {
        const response = await this.recordCompletedRaw(payload);
        return response.success;
    }
    recordCompletedRaw(payload) {
        const endpoint = this.getEndpoint('complete', 'complete');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, payload);
    }
    /**
     * Page-unload flush via <c>navigator.sendBeacon</c>. Fire-and-forget — the
     * browser ignores the response. Falls back to a regular POST if sendBeacon
     * is unavailable (e.g. SSR / older browsers).
     * POST resources/api/usage-logs/beacon
     */
    beacon(payload) {
        const endpoint = this.getEndpoint('beacon', 'beacon');
        const url = this.buildUrl(endpoint);
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            try {
                const absolute = `${this.http.getBaseUrl()}${url}`;
                const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
                navigator.sendBeacon(absolute, blob);
                return;
            }
            catch {
                // fall through to fetch
            }
        }
        void this.http.post(url, payload);
    }
}
UsageLogService.DEFAULT_SERVICE_PATH = 'resources';
UsageLogService.BASE_PATH = 'api/usage-logs';
//# sourceMappingURL=UsageLogService.js.map